/**
 * Timed Popup
 * Shows after a specific amount of time on page (45-60 seconds)
 */

import React, { useState, useEffect } from 'react';
import BasePopup from './BasePopup';
import PopupForm from './PopupForm';

export interface TimedPopupProps {
  enabled?: boolean;
  delay?: number; // Time to wait before showing (ms) - default 50 seconds
}

const TimedPopup: React.FC<TimedPopupProps> = ({
  enabled = true,
  delay = 50000, // 50 seconds
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    // Only stop showing if user has actually signed up
    const hasSignedUp = localStorage.getItem('landListingSignup');
    if (hasSignedUp) {
      setHasShown(true);
      return;
    }

    const timer = setTimeout(() => {
      setIsOpen(true);
      setHasShown(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [enabled, delay]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSuccess = () => {
    setIsOpen(false);
    // Mark that user has signed up - don't show any popups again
    localStorage.setItem('landListingSignup', 'true');
    alert('Success! Check your email for the free checklist.');
  };

  if (!enabled || hasShown) return null;

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title="🎁 Special Offer for Builders"
      subtitle="Get exclusive access to our land evaluation tools"
    >
      <div className="space-y-4">
        <p className="text-gray-600">
          Since you're still here, we'd like to offer you something valuable...
        </p>

        <div className="bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-purple-300 rounded-xl p-6">
          <h3 className="font-bold text-lg text-purple-900 mb-3">Free Builder's Bundle:</h3>
          <ul className="space-y-2 text-purple-800">
            <li className="flex items-start">
              <span className="text-purple-600 mr-2">📋</span>
              <span>Land Evaluation Checklist (PDF)</span>
            </li>
            <li className="flex items-start">
              <span className="text-purple-600 mr-2">📊</span>
              <span>ROI Calculator (Excel Template)</span>
            </li>
            <li className="flex items-start">
              <span className="text-purple-600 mr-2">📈</span>
              <span>Market Trends Report 2025</span>
            </li>
            <li className="flex items-start">
              <span className="text-purple-600 mr-2">💎</span>
              <span>Weekly development opportunities</span>
            </li>
          </ul>
        </div>

        <div className="text-sm text-gray-500 italic">
          "This checklist saved me 10+ hours on our last land acquisition" - Mike T., Builder
        </div>

        <PopupForm
          source="timed_popup"
          onSuccess={handleSuccess}
          buttonText="Claim My Free Bundle"
        />
      </div>
    </BasePopup>
  );
};

export default TimedPopup;
